import { createEnum } from '../helpers'
import { REVIEW_EVENT_TYPE_KEYS } from './review-events'

/**
 * The credit reviewer/approver inbox (FUZE-616): one feed that mixes every kind of request a user
 * owes a decision on, replacing the per-type tables.
 *
 * The vocabulary here is deliberately transport-only — keys, not copy. Labels and colours live in
 * the frontend (i18n + Tailwind) because a server-rendered string cannot be translated, and the
 * band a request falls in travels as its `DoaOptionKey` rather than a formatted
 * "$50,000 – $100,000 · Low risk" so the client can render it in the user's locale.
 */

// ── Task types ───────────────────────────────────────────────────────────────────────────────
/**
 * What a feed row is. Modelled as an open enum from the start so that adding a member is additive
 * on both sides of the wire — `initiative` joined with FUZE-632 and `blocked_orders` with CT-31,
 * each exactly that way.
 *
 * Adding a member is NOT free of obligations, though: each one needs a source in the feed and a
 * branch in `getCurrentVersionNumber`, or its rows silently never appear. `inboxSources.ci.test.ts`
 * in the backend fails the build when one is missing.
 */
export const INBOX_TASK_TYPES = ['single_clr', 'mass_clr', 'initiative', 'blocked_orders'] as const
export const INBOX_TASK_TYPE = createEnum(INBOX_TASK_TYPES)
export type InboxTaskType = (typeof INBOX_TASK_TYPES)[number]

// ── Stage ────────────────────────────────────────────────────────────────────────────────────
/**
 * What is being asked of the viewer, derived rather than stored. `review` and `approve` come from
 * the pending chain slot — the approver slot means "approve", every reviewer slot means "review".
 * This is the cut a reviewer actually works by: the type of the request matters far less than
 * whether they are the last signature or not.
 *
 * `prepare` is the one stage that is NOT a pending-slot match, because a preparer holds no chain
 * slot. It means the request came back for rework — sent back to the preparer, who has to adjust it
 * and resubmit. Without it a send-back is pending at no slot and so belongs to no stage, which is
 * how it ended up counted as resolved for the one person who still owed something.
 *
 * Ordered by where a request sits in the chain, so the tabs read left to right in workflow order.
 */
export const INBOX_STAGES = ['prepare', 'review', 'approve'] as const
export type InboxStage = (typeof INBOX_STAGES)[number]

// ── Filters ──────────────────────────────────────────────────────────────────────────────────
/**
 * `direct` = the viewer holds the pending slot as its primary; `delegated` = they hold only the
 * delegate counterpart, so they can act but the primary gets first pick.
 */
export const INBOX_SCOPES = ['all', 'direct', 'delegated'] as const
export type InboxScope = (typeof INBOX_SCOPES)[number]

/**
 * `latest` is newest-first and the default. `priority` orders by the fraction of each row's SLA
 * already spent, which is what lets a 25h-old inactivation outrank a week-old periodic review.
 *
 * Both are opt-in: the feed never reorders itself as things come due, because a queue that
 * rearranges under the reader is worse than a stale one.
 */
export const INBOX_SORTS = ['latest', 'priority'] as const
export type InboxSort = (typeof INBOX_SORTS)[number]

// ── Modes ────────────────────────────────────────────────────────────────────────────────────
/**
 * The inbox's two queues, never shown together. CLRs (single, mass, initiatives) are worked by
 * what stage they are at and how long since they last moved; a blocked order is worked by how long
 * it has before its shipping cutoff. Mixing them made both harder to read, so the inbox shows one
 * mode at a time.
 */
export const INBOX_MODES = ['clr', 'blocked_orders'] as const
export type InboxMode = (typeof INBOX_MODES)[number]

export function getInboxTaskMode(type: InboxTaskType): InboxMode {
  return type === 'blocked_orders' ? 'blocked_orders' : 'clr'
}

/**
 * Where a blocked order sits against its cutoff: the tabs of the blocked-orders mode. `missed` is
 * its own bucket rather than part of `urgent` — a missed cutoff and one about to be missed call for
 * different actions, and a pile of missed orders would otherwise bury the ones that can still ship.
 *
 * Ordered most pressing first, which is the order the tabs read in. The thresholds live beside the
 * cutoff arithmetic in `@repo/utils` (`getBlockedOrderBucket`).
 */
export const BLOCKED_ORDER_BUCKETS = ['missed', 'urgent', 'medium', 'good'] as const
export type BlockedOrderBucket = (typeof BLOCKED_ORDER_BUCKETS)[number]

// ── CLR kinds ────────────────────────────────────────────────────────────────────────────────
/**
 * What raised a single CLR: the nine system triggers, plus `manual` for one a human prepared.
 *
 * The nine ARE `REVIEW_EVENT_TYPE_KEYS`, unprojected, because every one of them auto-prepares a
 * review (`REVIEW_EVENT_PREPARES_CLR` is true for all of them). An earlier version mapped only four
 * onto a narrower vocabulary, which meant a CLR raised by a failed payment, a credit-index move, a
 * central-payer unlink or a reactivation was labelled "Periodic" — naming a real trigger as
 * something it is not. The lossy projection was the bug, not a simplification.
 *
 * A review that nothing raised — the scheduled sweep, and every row predating the triggers — has a
 * **null** kind rather than a `periodic` member. It is the absence of a trigger, not an eleventh one,
 * and modelling it as a member is what let it render as a category beside the real causes and be
 * offered as a filter. Null makes the compiler ask every consumer what to do about it; the answers
 * so far are "no chip" in the UI and `DEFAULT_CLR_SLA_HOURS` for ageing.
 *
 * Nothing stores this. `credit_limit_reviews.kind` existed briefly and was dropped; the value is
 * derived per query by `clrKindSql`, so there is no persisted vocabulary to keep in step.
 */
export const CLR_KINDS = [...REVIEW_EVENT_TYPE_KEYS, 'manual'] as const
export const CLR_KIND = createEnum(CLR_KINDS)
export type ClrKind = (typeof CLR_KINDS)[number]

// ── Service levels ───────────────────────────────────────────────────────────────────────────
/**
 * How long each kind may sit before it is late. Drives the age colour and the `priority` sort.
 *
 * These are wall-clock hours, not business hours: an inactivation has to clear in 24h because the
 * account is transacting at a limit the business no longer stands behind, and a weekend does not
 * change that.
 */
export const CLR_KIND_SLA_HOURS = {
  // The 24h band is reserved for "the limit live in SAP right now is wrong": the account is
  // transacting at a number the business no longer stands behind.
  accountDeactivated: 24,
  // Risk signals and structural changes: real, but the current limit is not itself wrong.
  accountActivated: 72,
  centralPayerLinked: 72,
  centralPayerUnlinked: 72,
  securityDepositsPosted: 72,
  securityDepositsRefunded: 72,
  failedPaymentCountChanged: 72,
  manual: 72,
  // Slow-moving: a new account has to be priced before it trades, and score drift is gradual.
  newAccount: 24 * 14,
  creditIndexScoreChanged: 24 * 14,
} as const satisfies Record<ClrKind, number>

/**
 * How long a review with no trigger may sit. A year, because a scheduled sweep has no forcing
 * event behind it — it is the floor the other bands are urgent relative to, and the reason a
 * null kind still sorts and colours like everything else instead of needing a special case.
 */
export const DEFAULT_CLR_SLA_HOURS = 24 * 365

/**
 * Mass runs carry no per-kind trigger, so they age against a nominal week. Deliberately shorter
 * than a periodic review: a run holds thousands of accounts and blocks each of them from entering
 * a single CLR while it sits.
 */
export const INBOX_BATCH_SLA_HOURS = 24 * 7

export const INBOX_URGENCIES = ['normal', 'due_soon', 'overdue'] as const
export type InboxUrgency = (typeof INBOX_URGENCIES)[number]

/** Fraction of the SLA already spent at which a row is "due soon" rather than "normal". */
export const INBOX_DUE_SOON_THRESHOLD = 0.6

/**
 * Fraction of the applicable SLA already spent — 1.0 is exactly on the deadline, above 1.0 is
 * late. Comparable across kinds and across task types, which is the whole point: it is the single
 * number the `priority` sort orders by.
 */
export function getInboxSlaConsumption(slaHours: number, receivedAt: Date, now: Date): number {
  return (now.getTime() - receivedAt.getTime()) / 3_600_000 / slaHours
}

export function getInboxUrgency(consumed: number): InboxUrgency {
  if (consumed >= 1) return 'overdue'
  if (consumed >= INBOX_DUE_SOON_THRESHOLD) return 'due_soon'
  return 'normal'
}

// ── Read state ───────────────────────────────────────────────────────────────────────────────
/**
 * A row is unread until the viewer opens it, and becomes unread AGAIN when new activity lands on
 * it — which is why read state stores the version the user last saw rather than a boolean. A
 * resubmission after a send-back is exactly the case a boolean would get wrong: the reader has
 * seen v1 and needs to be told about v2.
 */
export function isInboxTaskRead(
  lastReadVersionNumber: number | null,
  currentVersionNumber: number,
): boolean {
  return lastReadVersionNumber !== null && lastReadVersionNumber >= currentVersionNumber
}
